import React, { useState, useEffect, useRef } from 'react';
import { X, Calculator, CheckCircle, ArrowLeft, Save, Eye, AlertTriangle } from 'lucide-react';
import { createPortal } from 'react-dom';
import { supabase } from '../../src/lib/supabase';
import { normalizeMesReferencia } from './FinanceiroLayout';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { useToast } from '../../src/contexts/ToastContext';
import { useAuth } from '../../src/contexts/AuthContext';
import ConfirmationModal from '../../src/components/ConfirmationModal';
import { Fatura } from '../../types';
import LembretesViewer from './components/LembretesViewer';
import { escolherPagador } from '../../src/lib/pagadorBoleto';
import { fontePagadorDaUc } from '../../src/lib/pagadorUc';
import { buscarPagadorDaUc } from '../../src/services/pagadores';
import { pagadorDoAcordo, type PagadorAcordo } from '../../supabase/functions/_shared/pagadorAcordo.ts';
import { acordoSomaNaFaturaMensal } from '../../supabase/functions/_shared/parcelaNaFaturaVigor.ts';
import { buscarTrocasDaUc, clienteDaFaturaAposTroca, comoODonoAnteriorSaiu, contextoCobrancaDaFatura, type ContextoCobranca, type TrocaDonoUc } from '../../src/lib/trocaDonoUc';

/** Cobrança de acordo com "boletos no nome do responsável" (quem assumiu a dívida):
 *  o pagador é o do acordo, não o assinante/cadastro da UC, que seguem sendo do
 *  titular. Mesma regra da emissão e da edge do Asaas (_shared/pagadorAcordo.ts). */
async function pagadorDoAcordoDaFatura(parcelamentoOrigemId: string | null | undefined): Promise<PagadorAcordo | null> {
    if (!parcelamentoOrigemId) return null;
    const { data } = await supabase.from('parcelamentos').select('termo_dados').eq('id', parcelamentoOrigemId).maybeSingle();
    return pagadorDoAcordo(data?.termo_dados);
}
import { calcularValoresBoleto } from '../../src/lib/calculoBoletoVigor';
import { temInjecao, CAMPOS_ORIGEM_LIGADOS_A_INJECAO, camposZeradosSemInjecao } from '../../src/lib/faturaSemInjecao';
import { propsFundoModal } from '../../src/lib/backdropModal';
import { bloqueioCobrancaVigor } from '../../src/lib/cobrancaNoMesmoMes';
import { barrarSeTemBoletoVivo } from '../../src/lib/boletoVivoDaFatura';
import { fracaoParaPercentTexto, lerPercentDigitado } from '../../src/lib/descontoPercentual';
import { mesmoDesconto, percentTexto, precisaDecidirAlcance, registrarDescontoDaFatura, rotuloAlcance, type Descontos, type EscopoDesconto } from '../../src/lib/descontoHistorico';
import { useHistoricoDesconto } from '../../src/hooks/useHistoricoDesconto';
import { ListaHistoricoDesconto } from '../../src/components/HistoricoDescontoUc';
import AlteracoesCadastroUc from '../../src/components/AlteracoesCadastroUc';
import { hojeIso, textoRegraVencimento, vencimentoPelaRegra } from '../../src/lib/regraVencimentoUc';
import { gravarCorrecaoFatura, ucFoiAlterada, type ResultadoCorrecao } from '../../src/lib/correcaoDadosFatura';

type CampoDesconto = 'desconto_tarifa' | 'desconto_bandeira';

const textoDescontos = (d: Descontos) => `Tarifa ${percentTexto(d.desconto_tarifa)} · Bandeira ${percentTexto(d.desconto_bandeira)}`;

interface FaturaCalculationModalProps {
    isOpen: boolean;
    onClose: () => void;
    fatura: Fatura | null;
    onUpdate: () => void;
    onNext?: () => void;
}

const FaturaCalculationModal: React.FC<FaturaCalculationModalProps> = ({ isOpen, onClose, fatura, onUpdate, onNext }) => {
    const { addToast } = useToast();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [showBackConfirm, setShowBackConfirm] = useState(false);
    const [linkedParcela, setLinkedParcela] = useState<any | null>(null);
    // Preview não-bloqueante de quem vai ser cobrado (o assinante), pra o analista
    // conferir antes de calcular — sem exigir clique extra de confirmação.
    const [pagadorPreview, setPagadorPreview] = useState<{ nome: string; documento: string; origem: string } | null>(null);
    // Regra de vencimento do cadastro da UC, mostrada embaixo da data (e se preencheu ela).
    const [regraVencimento, setRegraVencimento] = useState<{ texto: string; aplicada: boolean } | null>(null);
    // Troca de dono da UC (venda do estabelecimento): com o pedido aberto, ou numa
    // fatura do dono anterior, quem paga e o desconto não são os do cadastro atual.
    // Ref porque o auto-save (debounce) lê fora do ciclo de render.
    const contextoCobrancaRef = useRef<ContextoCobranca | null>(null);
    const [avisoTroca, setAvisoTroca] = useState<string | null>(null);
    // UC de cliente próprio da Gran, ou avulso da Gran vivo no mesmo mês: a Vigor não
    // cobra — ver src/lib/cobrancaNoMesmoMes.ts (chamado #6000). O aviso é adiantado; a
    // conferência que vale é a do clique em Confirmar.
    const [bloqueioVigor, setBloqueioVigor] = useState<string | null>(null);
    // Guardrail de "sem injeção": só pausa o fluxo quando sobra resíduo de
    // juros/multa/parcelamento depois de zerar os campos ligados à injeção —
    // ver src/lib/faturaSemInjecao.ts.
    const [showSemInjecaoConfirm, setShowSemInjecaoConfirm] = useState(false);
    const [motivoSemInjecao, setMotivoSemInjecao] = useState('');
    const [pendingOverridesSemInjecao, setPendingOverridesSemInjecao] = useState<Record<string, unknown> | null>(null);

    // Desconto do cadastro (da UC, ou do dono anterior numa troca de dono). É a base
    // para saber se a fatura saiu do padrão.
    const [descontoCadastro, setDescontoCadastro] = useState({
        desconto_tarifa: 0.20,
        desconto_bandeira: 0.50
    });

    // Desconto diferente do cadastro: o analista diz se vale só nesta fatura ou daqui
    // pra frente, e por quê — ver src/lib/descontoHistorico.ts. Antes a fatura copiava
    // o desconto para a UC sem perguntar (caso Aline, UC 420455101230, SET/2026).
    const [ucId, setUcId] = useState<number | null>(null);
    const historico = useHistoricoDesconto(ucId);
    const [decisaoDesconto, setDecisaoDesconto] = useState<null | 'concluir' | 'confirmar'>(null);
    const [alcanceEscolhido, setAlcanceEscolhido] = useState<EscopoDesconto | null>(null);
    const [motivoDesconto, setMotivoDesconto] = useState('');
    const [alcanceUcBloqueado, setAlcanceUcBloqueado] = useState<string | null>(null);
    const [salvandoDecisao, setSalvandoDecisao] = useState(false);

    // Form State (initially populated from fatura or calculated defaults)
    // _faturaId carimba de qual fatura este formData veio. A fila de calculo troca a
    // fatura por baixo do modal (executarConfirmacao -> onNext) sem desmontar o
    // componente, o formData so e recarregado depois do fetch async da UC e o auto-save
    // tem debounce de 1s — sem o carimbo, um save agendado para a fatura anterior dispara
    // depois da troca e grava uc_codigo/cliente/vencimento antigos na linha nova. Foi o
    // que mandou a fatura JUL/2026 da UC 890601290 para a UC 377781201285 em 14/09/2026.
    const [formData, setFormData] = useState({
        _faturaId: '',
        desconto_tarifa: 0.20,
        desconto_bandeira: 0.50,
        // Base values needed for calculation (read-only mostly, but good to have)
        energia_injetada_gd1_kwh: 0,
        energia_injetada_gd2_kwh: 0,
        valor_tarifa_distribuidora: 0,
        tarifa_bandeira: 0,
        valor_parc_injet_s_desc: 0,
        valor_restituicao_imposto: 0,
        energia_nao_isenta: 0,
        valor_juros_boleto: 0,
        outros_multas_boleto: 0,
        valor_parcelamentos_boleto: 0,
        parcelamento_descricao: '',
        vencimento_boleto: '',
        cliente_nome: '',
        cliente_cpf_cnpj: '',
        uc_codigo: ''
    });

    // Texto dos campos de desconto enquanto o analista digita, em %. O formData segue
    // em fração (é o que o banco e o cálculo usam) e só muda quando o texto é um
    // percentual válido — vazio, "28," a meio caminho ou "0,28" não zeram nada.
    const [rascunhoDesconto, setRascunhoDesconto] = useState<Partial<Record<CampoDesconto, string>>>({});
    const descontoAoFocar = useRef<Partial<Record<CampoDesconto, number>>>({});

    // Calculated Results
    const [calculated, setCalculated] = useState({
        valor_consumo_scee_boleto: 0,
        valor_adc_bandeira_boleto: 0,
        valor_parc_injet_s_desc_boleto: 0,
        valor_restituicao_imposto_boleto: 0,
        valor_energia_nao_isenta_boleto: 0,
        valor_juros_boleto: 0,
        outros_multas_boleto: 0,
        valor_parcelamentos_boleto: 0,
        valor_boleto_vigor: 0,
        total_com_vigor: 0,
        economia: 0,
        custo_s_vigor: 0,
        consumo_total_gd_s_vigor: 0,
        valor_adc_bandeira_s_vigor: 0
    });

    // Fetch UC Data on Mount
    useEffect(() => {
        if (isOpen && fatura?.uc_codigo) {
            // O auto-save grava a fatura, o tempo real devolve a mesma fatura como objeto
            // novo e este recarregamento reescrevia o formulário com o que foi salvo 1s
            // antes — apagando o que o analista digitou nesse meio-tempo. Durante a
            // edição da MESMA fatura, a tela é a fonte da verdade; troca de fatura
            // (fila, onNext) continua recarregando.
            if (isEditing && String(fatura.id) === String(formData._faturaId)) return;
            setRascunhoDesconto({});
            fetchUCData();
            setBloqueioVigor(null);
            if (!fatura.fora_parceria_vigor) {
                bloqueioCobrancaVigor(supabase, fatura.uc_codigo, fatura.mes_referencia)
                    .then(setBloqueioVigor)
                    .catch(() => setBloqueioVigor(null));
            }
        }
    }, [isOpen, fatura]);

    const fetchUCData = async () => {
        setRegraVencimento(null);
        if (!fatura?.uc_codigo) return;
        setLoading(true);

        let newDiscounts = {
            desconto_tarifa: 0.20,
            desconto_bandeira: 0.50
        };

        try {
            // Mesma resolução do salvamento (handleSubmit) e do BoletoEmissionModal: a função
            // do banco casa exato ou normalizado e desempata UC duplicada. Comparar o texto
            // cru não achava a UC cadastrada formatada ("96.289.012-72") quando a fatura vem
            // só com dígitos: a tela abria com o desconto padrão 20%/50% no lugar do
            // cadastro, "Assinante não identificado" e sem parcelamento nem troca de dono.
            const { data: ucList } = await supabase.rpc('resolver_uc_por_codigo', { p_codigo: fatura.uc_codigo });
            const ucData: any = (Array.isArray(ucList) ? ucList[0] : ucList) || null;
            setUcId(ucData?.id ?? null);

            // Falha ao ler as trocas = segue o caminho de sempre (UC e negociação dela).
            const trocas: TrocaDonoUc[] = ucData?.id
                ? await buscarTrocasDaUc(supabase, ucData.id).catch(() => [])
                : [];
            const contexto = ucData ? contextoCobrancaDaFatura(ucData, fatura, trocas) : null;
            contextoCobrancaRef.current = contexto;
            const clienteTroca = ucData
                ? clienteDaFaturaAposTroca(fatura, ucData, trocas.filter(t => t.status === 'efetivada'))
                : null;
            setAvisoTroca(
                !contexto?.troca
                    ? null
                    : fatura.dono_anterior_troca_id
                        ? `Esta fatura é de ${contexto.cadastro.nome || 'o dono anterior'}, ${comoODonoAnteriorSaiu(contexto.troca)}. Ela continua no nome e com o desconto dele.`
                        : `A UC está em troca de dono (pedida para a negociação #${contexto.troca.negociacao_destino_id}). Até a análise de lá ser aprovada, a fatura ainda é de ${contexto.cadastro.nome || 'o dono atual'}, com o desconto dele.`
            );

            if (ucData && contexto) {
                newDiscounts = {
                    desconto_tarifa: contexto.descontos.desconto_tarifa ?? 0.20,
                    desconto_bandeira: contexto.descontos.desconto_bandeira ?? 0.50
                };

                setDescontoCadastro(newDiscounts);

                if (contexto.descontoEhDaUc && (ucData.desconto_tarifa === null || ucData.desconto_bandeira === null)) {
                    await registrarDescontoDaFatura(supabase, {
                        faturaId: fatura.id,
                        ucId: ucData.id,
                        escopo: 'uc',
                        novo: newDiscounts,
                        antes: { desconto_tarifa: ucData.desconto_tarifa, desconto_bandeira: ucData.desconto_bandeira },
                        motivo: 'Cadastro da UC estava sem desconto: preenchido com o padrão ao abrir o cálculo da fatura.',
                    }).catch(e => console.error('Erro ao preencher o desconto padrão na UC:', e));
                }
            }

            // --- BUSCA AUTOMÁTICA DE PARCELAMENTO ---
            let fetchedParcela = null;
            
            // 1. Verifica se esta fatura já tem parcela associada
            const { data: existingParcela } = await supabase
                .from('parcelamento_parcelas')
                .select('*, parcelamento:parcelamentos(id, numero_parcelas, descricao)')
                .eq('fatura_id', fatura.id)
                .maybeSingle();
            
            if (existingParcela) {
                fetchedParcela = existingParcela;
            } else if (ucData?.id) {
                // 2. Busca parcela pendente compatível com a UC e o mês de referência.
                // Só de acordo que já vale e que soma na fatura (quem escolheu somar
                // no boleto da Vigor, ou o acordo antigo): a parcela de proposta
                // ainda não assinada entrava aqui e era cobrada mesmo se o acordo
                // expirasse. A que já saiu em boleto próprio tem fatura_id.
                const mesRefNormal = normalizeMesReferencia(fatura.mes_referencia);
                const { data: candidatas } = await supabase
                    .from('parcelamento_parcelas')
                    .select('*, parcelamento:parcelamentos!inner(id, unidade_consumidora_id, numero_parcelas, descricao, termo_dados, dono_anterior_troca_id, status, cobranca_parcelas, faturas_origem_snapshot, termo_id_autentique)')
                    .eq('status', 'pendente')
                    .eq('tipo', 'parcela')
                    .is('fatura_id', null)
                    .eq('mes_referencia', mesRefNormal)
                    .eq('parcelamento.unidade_consumidora_id', ucData.id)
                    .order('numero_parcela');
                const pendingParcela = (candidatas || []).find((p: any) => acordoSomaNaFaturaMensal(p.parcelamento)) ?? null;

                // Parcela de acordo cujo boleto é do RESPONSÁVEL (quem assumiu a
                // dívida) não entra na fatura mensal: ela é do titular, e a parcela
                // sairia no boleto da pessoa errada. Ela é cobrada à parte.
                //
                // Acordo do dono anterior da UC (troca de dono) também não entra na
                // fatura de quem comprou o estabelecimento: sai separado, no nome dele.
                const acordoDoDonoAnterior = !!(pendingParcela as any)?.parcelamento?.dono_anterior_troca_id
                    && !fatura.dono_anterior_troca_id;
                if (pendingParcela && !acordoDoDonoAnterior && !pagadorDoAcordo((pendingParcela as any).parcelamento?.termo_dados)) {
                    fetchedParcela = pendingParcela;
                }
            }

            setLinkedParcela(fetchedParcela);

            // Carregar valores de parcelamento padrão se ainda não configurados na fatura
            let initialValParcelamento = fatura.valor_parcelamentos_boleto !== null ? Number(fatura.valor_parcelamentos_boleto) : 0;
            let initialDescParcelamento = fatura.parcelamento_descricao || '';

            if (fatura.valor_parcelamentos_boleto === null && fetchedParcela) {
                initialValParcelamento = Number(fetchedParcela.valor);
                const parentDesc = fetchedParcela.parcelamento?.descricao || 'Acordo';
                initialDescParcelamento = `Parcela ${fetchedParcela.numero_parcela}/${fetchedParcela.parcelamento?.numero_parcelas || '?'} - ${parentDesc}`;
            }
            // -----------------------------------------

            // Regra de vencimento do cadastro da UC (src/lib/regraVencimentoUc.ts). Vale
            // enquanto o vencimento ainda é o da Equatorial, que a validação copia; data
            // que alguém já escolheu à mão fica como está.
            const vencEquatorial = fatura.data_vencimento ? String(fatura.data_vencimento).slice(0, 10) : '';
            const vencAtual = fatura.vencimento_boleto ? String(fatura.vencimento_boleto).slice(0, 10) : '';
            const vencPelaRegra = vencimentoPelaRegra(ucData, vencEquatorial, hojeIso());
            const usaRegra = !!vencPelaRegra && (!vencAtual || vencAtual === vencEquatorial);
            setRegraVencimento(textoRegraVencimento(ucData) ? { texto: textoRegraVencimento(ucData)!, aplicada: usaRegra && vencPelaRegra !== vencAtual } : null);

            setFormData(prev => ({
                ...prev,
                _faturaId: fatura.id,
                desconto_tarifa: fatura.desconto_tarifa ?? newDiscounts.desconto_tarifa,
                desconto_bandeira: fatura.desconto_bandeira ?? newDiscounts.desconto_bandeira,
                energia_injetada_gd1_kwh: Number(fatura.energia_injetada_gd1_kwh || 0),
                energia_injetada_gd2_kwh: Number(fatura.energia_injetada_gd2_kwh || 0),
                valor_tarifa_distribuidora: Number(fatura.valor_tarifa_distribuidora || 0),
                tarifa_bandeira: Number(fatura.tarifa_bandeira || 0),
                valor_parc_injet_s_desc: Number(fatura.valor_parc_injet_s_desc || 0),
                valor_restituicao_imposto: Number(fatura.valor_restituicao_imposto || 0),
                energia_nao_isenta: Number(fatura.energia_nao_isenta || 0),
                valor_juros_boleto: Number(fatura.valor_juros_boleto || 0),
                outros_multas_boleto: Number(fatura.outros_multas_boleto || 0),
                valor_parcelamentos_boleto: initialValParcelamento,
                parcelamento_descricao: initialDescParcelamento,
                vencimento_boleto: usaRegra ? vencPelaRegra! : (fatura.vencimento_boleto || fatura.data_vencimento || ''),
                // Até a Equatorial trocar o titular, a conta do dono novo chega no nome
                // do antigo; a fatura passa a levar o nome de quem paga a Vigor.
                cliente_nome: clienteTroca?.nome ?? (fatura.cliente_nome || ''),
                cliente_cpf_cnpj: clienteTroca?.documento ?? (fatura.cliente_cpf_cnpj || ''),
                uc_codigo: fatura.uc_codigo || ''
            }));

            // Preview de quem vai ser cobrado — mesma cascata de handleSubmit, só que
            // aqui é só para exibir, não grava nada.
            let negociacaoPreview: any = null;
            if (contexto?.negociacaoId) {
                const { data: neg } = await supabase
                    .from('negociacoes')
                    .select('cliente_nome, cpf_cnpj')
                    .eq('id', contexto.negociacaoId)
                    .maybeSingle();
                negociacaoPreview = neg;
            }
            const pagadorAcordoPreview = await pagadorDoAcordoDaFatura(fatura.parcelamento_origem_id);
            // Pagador da UC antes do assinante (ver src/lib/pagadorUc.ts).
            const fonteUcPreview = ucData?.id
                ? fontePagadorDaUc(await buscarPagadorDaUc(ucData.id), !!contexto?.troca)
                : null;
            const preview = pagadorAcordoPreview
                ? { nome: pagadorAcordoPreview.nome, documento: pagadorAcordoPreview.documento, origem: 'responsável pela dívida (acordo de parcelamento)' }
                : escolherPagador([
                    ...(fonteUcPreview ? [fonteUcPreview] : []),
                    { origem: 'assinante (termo Vigor)', nome: negociacaoPreview?.cliente_nome, documento: negociacaoPreview?.cpf_cnpj },
                    { origem: 'cadastro da UC', nome: contexto?.cadastro.nome, documento: contexto?.cadastro.documento },
                ]);
            setPagadorPreview(preview.nome ? preview : null);

        } catch (error) {
            console.error('Error fetching UC/Parcelamento data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Calculate Values
    useEffect(() => {
        setCalculated(calcularValoresBoleto(formData));
    }, [formData]);


    const maskCPF = (value: string) => {
        return value
            .replace(/\D/g, '')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})/, '$1-$2')
            .replace(/(-\d{2})\d+?$/, '$1');
    };

    const maskCNPJ = (value: string) => {
        return value
            .replace(/\D/g, '')
            .replace(/^(\d{2})(\d)/, '$1.$2')
            .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
            .replace(/\.(\d{3})(\d)/, '.$1/$2')
            .replace(/(\d{4})(\d)/, '$1-$2')
            .replace(/(-\d{2})\d+?$/, '$1');
    };

    const maskCPFCNPJ = (value: string) => {
        const pureValue = value.replace(/\D/g, '');
        if (pureValue.length <= 11) {
            return maskCPF(value);
        }
        return maskCNPJ(value);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type } = e.target;

        if (name === 'cliente_cpf_cnpj') {
            setFormData(prev => ({
                ...prev,
                [name]: maskCPFCNPJ(value)
            }));
            return;
        }

        // O código da UC é texto: o parseFloat abaixo gravava "0" ao apagar o campo para
        // redigitar e cortava o número no primeiro ponto ou traço (fatura 15210, 29/10/2026).
        if (name === 'cliente_nome' || name === 'parcelamento_descricao' || name === 'uc_codigo') {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: type === 'date' ? value : (parseFloat(value) || 0)
        }));
    };

    const handleDescontoFocus = (campo: CampoDesconto) => {
        descontoAoFocar.current[campo] = formData[campo];
    };

    const handleDescontoChange = (campo: CampoDesconto, texto: string) => {
        setRascunhoDesconto(prev => ({ ...prev, [campo]: texto }));
        const lido = lerPercentDigitado(texto);
        // Vazio ou inválido volta ao valor de antes de entrar no campo, e não ao último
        // válido: "0,28" passa por "0" (0% é válido), e o auto-save gravaria 0% na
        // fatura e na UC enquanto a tela mostra o aviso de formato.
        const fracao = lido.tipo === 'ok' ? lido.fracao : descontoAoFocar.current[campo];
        if (fracao !== undefined) setFormData(prev => ({ ...prev, [campo]: fracao }));
    };

    // Ao sair do campo, texto válido ou vazio volta a mostrar o valor em uso. O inválido
    // fica, com o aviso, para o analista ver que não foi aceito.
    const handleDescontoBlur = (campo: CampoDesconto) => {
        setRascunhoDesconto(prev => {
            const texto = prev[campo];
            if (texto === undefined || lerPercentDigitado(texto).tipo === 'erro') return prev;
            const { [campo]: _, ...resto } = prev;
            return resto;
        });
    };

    const erroDesconto = (campo: CampoDesconto): string | null => {
        const texto = rascunhoDesconto[campo];
        if (texto === undefined) return null;
        const lido = lerPercentDigitado(texto);
        return lido.tipo === 'erro' ? lido.mensagem : null;
    };

    // Último erro de gravação já avisado: o salvamento automático roda a cada pausa da
    // digitação e repetiria o mesmo aviso a cada segundo.
    const ultimoAvisoRef = useRef('');
    const avisarErroGravacao = (r: ResultadoCorrecao | null, sempre = false) => {
        const texto = [r?.erro && `Os dados não foram salvos: ${r.erro}`, r?.erroUc && `O número da UC não foi salvo: ${r.erroUc}`]
            .filter(Boolean).join(' ');
        if (!texto) { ultimoAvisoRef.current = ''; return; }
        if (!sempre && texto === ultimoAvisoRef.current) return;
        ultimoAvisoRef.current = texto;
        addToast(texto, 'error', 15000);
    };

    // `comUc`: o número da UC só vai ao concluir a edição, ao fechar e ao confirmar o
    // cálculo (ver src/lib/correcaoDadosFatura.ts). O erro do banco volta para quem chamou.
    const saveChanges = async (overrides?: Record<string, unknown>, opcoes?: { comUc?: boolean }): Promise<ResultadoCorrecao | null> => {
        if (!fatura) return null;
        // So grava se o formData carregado ainda for o desta fatura (ver _faturaId).
        if (formData._faturaId !== fatura.id) return null;
        try {
            // O desconto fica só na fatura. O cadastro da UC muda apenas quando o
            // analista escolhe "Nesta e nas próximas" (confirmarDecisaoDesconto) — antes
            // o auto-save copiava para a UC sem perguntar, e um ajuste de um mês virava
            // o desconto do cliente.
            return await gravarCorrecaoFatura(supabase, fatura.id, {
                desconto_tarifa: formData.desconto_tarifa,
                desconto_bandeira: formData.desconto_bandeira,
                vencimento_boleto: formData.vencimento_boleto,
                cliente_nome: formData.cliente_nome,
                cliente_cpf_cnpj: formData.cliente_cpf_cnpj,
                parcelamento_descricao: formData.parcelamento_descricao,
                ...calculated,
                ...overrides
            }, opcoes?.comUc ? { digitada: formData.uc_codigo, atual: fatura.uc_codigo } : undefined);
        } catch (error: any) {
            console.error("Error saving calculation:", error);
            return { erro: error?.message || 'Falha de conexão ao salvar.', erroUc: null };
        }
    };

    // Auto-save on edit is implied by previous requirement, implementing equivalent logic
    // `fatura?.id`, e não o objeto: cada gravação volta pelo tempo real como objeto novo
    // da mesma fatura e, com o objeto aqui, agendava outra gravação igual — um ciclo a
    // cada ~2s enquanto o modal ficasse em edição.
    useEffect(() => {
        if (!isEditing || !fatura) return;
        const timer = setTimeout(() => {
            saveChanges().then(r => avisarErroGravacao(r));
        }, 1000);
        return () => clearTimeout(timer);
    }, [formData, calculated, isEditing, fatura?.id]);

    // Fechar (X ou clique fora) no meio da edição grava o que estava na tela, número da
    // UC junto — ele não vai no salvamento automático.
    const fechar = () => {
        if (isEditing) {
            saveChanges(undefined, { comUc: true }).then(r => avisarErroGravacao(r, true));
            setIsEditing(false);
        }
        onClose();
    };


    const handleStopEditing = async () => {
        const erro = erroDesconto('desconto_tarifa') || erroDesconto('desconto_bandeira');
        if (erro) {
            addToast(`Corrija o desconto antes de concluir: ${erro}`, 'warning');
            return;
        }
        if (precisaDecidirDesconto()) {
            abrirDecisaoDesconto('concluir');
            return;
        }
        await concluirEdicao();
    };

    // Recusa do banco mantém a edição aberta com o aviso: antes a tela saía da edição,
    // recarregava a fatura e o número digitado sumia sem explicação.
    const concluirEdicao = async () => {
        const r = await saveChanges(undefined, { comUc: true });
        if (r?.erro || r?.erroUc) {
            avisarErroGravacao(r, true);
            return;
        }
        ultimoAvisoRef.current = '';
        setRascunhoDesconto({});
        setIsEditing(false);
        onUpdate();
    };

    // Sem UC achada não há onde registrar; a emissão já trava nesse caso.
    const precisaDecidirDesconto = () =>
        !!fatura && !!ucId && precisaDecidirAlcance(formData, descontoCadastro, historico.registros, fatura.id);

    const abrirDecisaoDesconto = (continuar: 'concluir' | 'confirmar') => {
        const descontoEhDaUc = contextoCobrancaRef.current?.descontoEhDaUc ?? true;
        setAlcanceUcBloqueado(
            !descontoEhDaUc
                ? 'Esta fatura é de troca de dono: o desconto dela é do dono anterior, não do cadastro atual da UC.'
                : fatura && formData.uc_codigo && ucFoiAlterada(formData.uc_codigo, fatura.uc_codigo)
                    ? 'O código da UC foi mudado nesta tela. Ajuste o desconto no cadastro da UC certa.'
                    : null
        );
        setAlcanceEscolhido(null);
        setMotivoDesconto('');
        setDecisaoDesconto(continuar);
    };

    const fecharDecisaoDesconto = () => {
        setDecisaoDesconto(null);
        setAlcanceEscolhido(null);
        setMotivoDesconto('');
    };

    // Mudou sem querer: a fatura volta para o desconto do cadastro e nada é registrado.
    // Fora da edição, o próximo "Confirmar cálculo" grava (executarConfirmacao → saveChanges).
    const voltarAoDescontoDoCadastro = () => {
        setRascunhoDesconto({});
        setFormData(prev => ({ ...prev, ...descontoCadastro }));
        fecharDecisaoDesconto();
    };

    const confirmarDecisaoDesconto = async () => {
        if (!fatura || !ucId || !alcanceEscolhido || !motivoDesconto.trim()) return;
        const continuar = decisaoDesconto;
        const novo = { desconto_tarifa: formData.desconto_tarifa, desconto_bandeira: formData.desconto_bandeira };
        setSalvandoDecisao(true);
        try {
            // Grava antes de registrar: "só nesta fatura" precisa do desconto já na fatura.
            const gravacao = await saveChanges();
            if (gravacao?.erro) throw new Error(gravacao.erro);
            await registrarDescontoDaFatura(supabase, {
                faturaId: fatura.id,
                ucId,
                escopo: alcanceEscolhido,
                novo,
                antes: descontoCadastro,
                motivo: motivoDesconto.trim(),
            });
            if (alcanceEscolhido === 'uc') setDescontoCadastro(novo);
            await historico.recarregar();
            fecharDecisaoDesconto();
            if (continuar === 'concluir') await concluirEdicao();
            else if (continuar === 'confirmar') await handleConfirm(true);
        } catch (error: any) {
            addToast(`Não deu para registrar o desconto: ${error?.message || 'erro desconhecido'}`, 'error', 10000);
        } finally {
            setSalvandoDecisao(false);
        }
    };


    const handleConfirm = async (descontoJaDecidido = false) => {
        if (!fatura) return;

        // Fatura com desconto fora do cadastro sem dizer o alcance (ex.: fechou a tela
        // no meio da edição). Sem isso o boleto sairia sem registro do porquê.
        if (!descontoJaDecidido && precisaDecidirDesconto()) {
            abrirDecisaoDesconto('confirmar');
            return;
        }

        // Sem injeção no ciclo, a Vigor não tem o que cobrar — só existe a fatura
        // da distribuidora. Sem esta trava, resíduo de um cálculo anterior (ou de
        // uma duplicata da mesma UC) deixa valor_boleto_vigor positivo mesmo sem
        // nenhum boleto real, e a fatura vira "Vencido" fantasma quando a data
        // passa. Ver src/lib/faturaSemInjecao.ts.
        if (!temInjecao(formData)) {
            const recalculado = calcularValoresBoleto({ ...formData, ...CAMPOS_ORIGEM_LIGADOS_A_INJECAO });
            if (recalculado.valor_boleto_vigor > 0) {
                // Sobrou juros/multa/parcelamento mesmo sem injeção — pode ser
                // dívida real (não zerar sozinho). Pausa e pede confirmação.
                setPendingOverridesSemInjecao({ ...CAMPOS_ORIGEM_LIGADOS_A_INJECAO, ...recalculado });
                setShowSemInjecaoConfirm(true);
                return;
            }
            await executarConfirmacao(camposZeradosSemInjecao(fatura), 'CALCULATE_BOLETO_SEM_INJECAO');
            return;
        }

        await executarConfirmacao(undefined, 'CALCULATE_BOLETO');
    };

    const confirmarSemInjecaoComResiduo = async () => {
        if (!motivoSemInjecao.trim()) {
            addToast('Por favor, informe o motivo para confirmar mesmo sem injeção.', 'warning');
            return;
        }
        const overrides = pendingOverridesSemInjecao;
        setShowSemInjecaoConfirm(false);
        await executarConfirmacao(overrides || undefined, 'CONFIRM_CALCULO_SEM_INJECAO_COM_RESIDUO', { motivo: motivoSemInjecao.trim() });
        setPendingOverridesSemInjecao(null);
        setMotivoSemInjecao('');
    };

    const executarConfirmacao = async (overrides: Record<string, unknown> | undefined, acao: string, logExtra?: Record<string, unknown>) => {
        if (!fatura) return;
        setLoading(true);
        try {
            // Primeiro de tudo, antes da parcela e de gravar valores: a tela pode estar
            // velha. Em 16/10/2026 a fatura 14192 foi recalculada 4 minutos depois de o
            // boleto 6816 sair e voltou da fila de envio para "Com boleto calculado".
            await barrarSeTemBoletoVivo(fatura.id, 'calcular');

            // Consulta nova, não o aviso da abertura: a marca da UC ou o avulso da Gran
            // podem ter mudado com o modal aberto.
            if (!fatura.fora_parceria_vigor) {
                const bloqueio = await bloqueioCobrancaVigor(supabase, fatura.uc_codigo, fatura.mes_referencia);
                setBloqueioVigor(bloqueio);
                if (bloqueio) throw new Error(bloqueio);
            }

            // Parcela do acordo: reivindica ANTES de gravar o valor. A varredura manda
            // em boleto separado a parcela que não entrou em nenhum boleto até 7 dias
            // antes de vencer (efetivar-parcelamentos) — mesmo UPDATE condicional, só
            // um dos dois fica com ela. Sem valor na fatura, a parcela não fica presa
            // aqui: volta a pendente para poder sair separada.
            const valorParcelaNaFatura = Number(overrides?.valor_parcelamentos_boleto ?? formData.valor_parcelamentos_boleto) || 0;
            if (linkedParcela && valorParcelaNaFatura > 0 && linkedParcela.fatura_id == null) {
                const { data: peguei, error: erroParcela } = await supabase
                    .from('parcelamento_parcelas')
                    .update({ status: 'provisionado', fatura_id: fatura.id })
                    .eq('id', linkedParcela.id)
                    .eq('status', 'pendente')
                    .is('fatura_id', null)
                    .select('id');
                if (erroParcela) throw new Error(erroParcela.message);
                if (!peguei || peguei.length === 0) {
                    const semParcela = { ...formData, valor_parcelamentos_boleto: 0, parcelamento_descricao: '' };
                    setFormData(semParcela);
                    setLinkedParcela(null);
                    await saveChanges({ ...calcularValoresBoleto(semParcela), parcelamento_descricao: '' });
                    throw new Error(`A parcela ${linkedParcela.numero_parcela} do acordo já saiu em boleto separado. O valor dela saiu desta fatura — confira e confirme de novo.`);
                }
            } else if (linkedParcela && valorParcelaNaFatura <= 0 && String(linkedParcela.fatura_id) === String(fatura.id)) {
                await supabase
                    .from('parcelamento_parcelas')
                    .update({ status: 'pendente', fatura_id: null })
                    .eq('id', linkedParcela.id)
                    .eq('fatura_id', fatura.id)
                    .eq('status', 'provisionado');
            }

            // Ensure connection and values are saved. Sem checar, o cálculo seguia com os
            // valores antigos no banco quando a gravação era recusada.
            const gravacao = await saveChanges(overrides, { comUc: true });
            if (gravacao?.erro) throw new Error(gravacao.erro);
            if (gravacao?.erroUc) throw new Error(`o número da UC não foi salvo: ${gravacao.erroUc}`);

            // O boleto sai no nome de quem ASSINOU o termo com a Vigor (o assinante,
            // registrado na negociação vinculada à UC) — nunca no titular da fatura da
            // Equatorial. São papéis deliberadamente distintos no negócio: existem
            // assinantes ativos cuja conta de luz continua no nome de outra pessoa (quem
            // não trocou a titularidade na distribuidora); o titular só está no sistema
            // para fins de registro. Caso Darcy/Bruno, UC 69401701217, ago/2026: cobrança
            // saiu no nome da titular da conta de luz em vez do assinante.
            //
            // Nome e CPF/CNPJ vêm SEMPRE da mesma fonte: pegar o nome de um lugar e o
            // documento de outro foi o que emitiu os boletos da AFCOM no CNPJ da ARE —
            // ver src/lib/pagadorBoleto.ts.
            const targetUc = formData.uc_codigo || fatura.uc_codigo;
            let ucData: any = null;
            let negociacaoData: any = null;
            let contexto: ContextoCobranca | null = null;
            if (targetUc) {
                // Mesma resolução do BoletoEmissionModal: a função do banco casa exato ou
                // normalizado nos dois campos e desempata UC duplicada.
                const { data: ucList } = await supabase.rpc('resolver_uc_por_codigo', { p_codigo: targetUc });
                ucData = (Array.isArray(ucList) ? ucList[0] : ucList) || null;

                // Troca de dono: com o pedido aberto a UC já está na negociação nova,
                // mas o boleto ainda é do dono anterior (ver contextoCobrancaDaFatura).
                const trocas: TrocaDonoUc[] = ucData?.id
                    ? await buscarTrocasDaUc(supabase, ucData.id).catch(() => [])
                    : [];
                contexto = ucData ? contextoCobrancaDaFatura(ucData, fatura, trocas) : null;

                if (contexto?.negociacaoId) {
                    const { data: neg } = await supabase
                        .from('negociacoes')
                        .select('cliente_nome, cpf_cnpj')
                        .eq('id', contexto.negociacaoId)
                        .maybeSingle();
                    negociacaoData = neg;
                }
            }

            const pagadorAcordo = await pagadorDoAcordoDaFatura(fatura.parcelamento_origem_id);
            // Pagador da UC antes do assinante (ver src/lib/pagadorUc.ts).
            const fonteUc = ucData?.id
                ? fontePagadorDaUc(await buscarPagadorDaUc(ucData.id), !!contexto?.troca)
                : null;
            const pagador = pagadorAcordo
                ? { nome: pagadorAcordo.nome, documento: pagadorAcordo.documento, origem: 'responsável pela dívida (acordo de parcelamento)' }
                : escolherPagador([
                    ...(fonteUc ? [fonteUc] : []),
                    { origem: 'assinante (termo Vigor)', nome: negociacaoData?.cliente_nome, documento: negociacaoData?.cpf_cnpj },
                    { origem: 'cadastro da UC', nome: contexto?.cadastro.nome ?? ucData?.nome_cliente, documento: contexto?.cadastro.documento ?? ucData?.cpf_cnpj_cliente },
                ]);

            // O banco também barra UC de cliente próprio da Gran e duplicata. Sem checar o
            // erro, a tela fechava como se tivesse calculado e a fatura ficava onde estava.
            const { error: erroStatus } = await supabase.from('faturas').update({
                status_fatura: 'Com boleto calculado',
                nome_cliente_boleto: pagador.nome,
                cpf_cnpj_cliente_boleto: pagador.documento
            }).eq('id', fatura.id);
            if (erroStatus) throw new Error(erroStatus.message);
            // A parcela do acordo já foi ligada a esta fatura lá em cima, antes de gravar o valor.

            await logAction(user?.id || 'SYSTEM', acao, { fatura_id: fatura.id, ...logExtra });
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'calcular_fatura', origem: 'acao' });
            onUpdate();
            if (onNext) {
                onNext();
            } else {
                onClose();
            }
        } catch (error: any) {
            console.error(error);
            addToast(error?.message ? `Erro ao confirmar cálculo: ${error.message}` : 'Erro ao confirmar cálculo.', 'error', 10000);
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        if (!fatura) return;
        setShowBackConfirm(true);
    };

    const executeBack = async () => {
        if (!fatura) return;
        setShowBackConfirm(false);

        setLoading(true);
        try {
            await barrarSeTemBoletoVivo(fatura.id, 'retroceder');

            // O erro do UPDATE precisa ser checado: o banco recusa voltar de etapa uma
            // fatura com boleto vivo, e sem isto a tela registrava o retrocesso assim mesmo.
            const { error: erroUpdate } = await supabase.from('faturas').update({
                status_fatura: 'Fatura disponível'
            }).eq('id', fatura.id);
            if (erroUpdate) throw erroUpdate;

            await logAction(user?.id || 'SYSTEM', 'REVERT_INVOICE_STATUS', { fatura_id: fatura.id, new_status: 'Fatura disponível' });
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'reverter_status_fatura', origem: 'acao' });
            onUpdate();
            onClose();
        } catch (error: any) {
            console.error(error);
            addToast(error?.message ? `Erro ao retroceder: ${error.message}` : 'Erro ao retroceder status.', 'error', 12000);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !fatura) return null;

    const confirmBackModal = (
        <ConfirmationModal
            isOpen={showBackConfirm}
            onClose={() => setShowBackConfirm(false)}
            onConfirm={executeBack}
            title="Retroceder Etapa?"
            description='Tem certeza que deseja retroceder para "Fatura disponível"? Os cálculos atuais podem ser perdidos se não salvos.'
            confirmText="Sim, retroceder"
            cancelText="Cancelar"
            variant="warning"
        />
    );

    const semInjecaoConfirmModal = (
        <ConfirmationModal
            isOpen={showSemInjecaoConfirm}
            onClose={() => { setShowSemInjecaoConfirm(false); setPendingOverridesSemInjecao(null); setMotivoSemInjecao(''); }}
            onConfirm={confirmarSemInjecaoComResiduo}
            title="Fatura sem injeção neste ciclo"
            description={
                <div className="text-left space-y-3">
                    <p>
                        Esta UC não teve injeção de energia neste ciclo (só existe a fatura da
                        distribuidora), mas ainda sobra <strong>R$ {Number(pendingOverridesSemInjecao?.valor_boleto_vigor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong> de
                        juros/multa/parcelamento no boleto. Confirme só se essa cobrança residual for real.
                    </p>
                    <div className="text-xs grid grid-cols-3 gap-2">
                        <span>Juros: R$ {Number(pendingOverridesSemInjecao?.valor_juros_boleto || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                        <span>Multa: R$ {Number(pendingOverridesSemInjecao?.outros_multas_boleto || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                        <span>Parcelamento: R$ {Number(pendingOverridesSemInjecao?.valor_parcelamentos_boleto || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <textarea
                        value={motivoSemInjecao}
                        onChange={(e) => setMotivoSemInjecao(e.target.value)}
                        placeholder="Motivo (ex: parcela 2/5 do acordo permanece mesmo sem injeção este mês)"
                        className="w-full border border-gray-200 dark:border-gray-600 rounded-lg p-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        rows={2}
                    />
                </div>
            }
            confirmText="Confirmar cálculo mesmo assim"
            cancelText="Cancelar"
            variant="warning"
        />
    );

    const descontoFatura = { desconto_tarifa: formData.desconto_tarifa, desconto_bandeira: formData.desconto_bandeira };
    const descontoForaDoCadastro = !!ucId && !mesmoDesconto(descontoFatura, descontoCadastro);
    const registroDestaFatura = historico.registros.find(r => r.fatura_id != null && String(r.fatura_id) === String(fatura.id));
    const alcanceDestaFatura = descontoForaDoCadastro && !precisaDecidirDesconto() && registroDestaFatura
        ? registroDestaFatura
        : null;

    const opcaoAlcance = (valor: EscopoDesconto, titulo: string, detalhe: string, bloqueio: string | null) => {
        const ativo = alcanceEscolhido === valor;
        return (
            <button
                type="button"
                disabled={!!bloqueio}
                onClick={() => setAlcanceEscolhido(valor)}
                aria-pressed={ativo}
                className={`w-full text-left rounded-xl border-2 p-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${ativo ? 'border-primary bg-primary/5' : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'}`}
            >
                <span className="block text-sm font-bold text-gray-900 dark:text-white">{titulo}</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{bloqueio ?? detalhe}</span>
            </button>
        );
    };

    const decisaoDescontoModal = (
        <ConfirmationModal
            isOpen={!!decisaoDesconto}
            onClose={fecharDecisaoDesconto}
            onConfirm={confirmarDecisaoDesconto}
            title="Desconto diferente do cadastro"
            description={
                <div className="text-left space-y-4">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 p-2.5">
                            <span className="block text-[10px] font-bold uppercase text-gray-400">Cadastro da UC</span>
                            <span className="font-semibold text-gray-800 dark:text-gray-200">{textoDescontos(descontoCadastro)}</span>
                        </div>
                        <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 p-2.5">
                            <span className="block text-[10px] font-bold uppercase text-amber-600 dark:text-amber-400">Esta fatura</span>
                            <span className="font-semibold text-gray-800 dark:text-gray-200">{textoDescontos(descontoFatura)}</span>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Este desconto vale para:</p>
                        {opcaoAlcance('fatura', 'Só nesta fatura',
                            `O cadastro da UC continua com ${textoDescontos(descontoCadastro)}. As próximas faturas usam o do cadastro.`, null)}
                        {opcaoAlcance('uc', 'Nesta e nas próximas faturas',
                            `Muda o cadastro da UC para ${textoDescontos(descontoFatura)}.`, alcanceUcBloqueado)}
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">Motivo</label>
                        <textarea
                            value={motivoDesconto}
                            onChange={e => setMotivoDesconto(e.target.value)}
                            placeholder="Obrigatório. Ex.: desconto de 30% só em setembro, combinado com o cliente pela demora no primeiro desconto."
                            className="w-full border border-gray-200 dark:border-gray-600 rounded-lg p-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            rows={2}
                        />
                    </div>
                    <button type="button" onClick={voltarAoDescontoDoCadastro} className="text-xs font-semibold text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline">
                        Mudei sem querer: voltar para o desconto do cadastro
                    </button>
                </div>
            }
            confirmText="Registrar e continuar"
            cancelText="Cancelar"
            variant="warning"
            isLoading={salvandoDecisao}
            confirmDisabled={!alcanceEscolhido || !motivoDesconto.trim()}
        />
    );

    return (
        <>
            {confirmBackModal}
            {semInjecaoConfirmModal}
            {decisaoDescontoModal}
            {createPortal(
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4"
                    style={{ zIndex: 9999 }}
                    {...propsFundoModal(() => fechar())}
                >
                    <div
                        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto flex flex-col"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="p-8 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800 sticky top-0 z-10">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                                    <Calculator className="text-primary" />
                                    Calcule o boleto
                                </h2>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    {fatura.cliente_nome} • {fatura.uc_codigo} • {fatura.mes_referencia}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                {fatura.pdf_fatura && (
                                    <button
                                        onClick={() => window.open(fatura.pdf_fatura, '_blank')}
                                        className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-secondary hover:bg-secondary/80 rounded-lg transition-colors mr-2"
                                    >
                                        <Eye size={16} />
                                        Ver PDF da fatura
                                    </button>
                                )}
                                <button onClick={fechar} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors">
                                    <X size={24} className="text-gray-400" />
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6 space-y-8">
                            <LembretesViewer uc_codigo={fatura.uc_codigo} mes_referencia={fatura.mes_referencia} fatura_id={fatura.id} />

                            {linkedParcela && (
                                <div className="p-4 bg-blue-50 dark:bg-blue-950/40 border border-blue-100 dark:border-blue-900/50 rounded-2xl flex gap-3.5 items-start animate-fade-in">
                                    <div className="p-2.5 bg-blue-500/10 rounded-xl text-blue-600 dark:text-blue-400 mt-0.5 shrink-0">
                                        <AlertTriangle size={20} />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-bold text-blue-900 dark:text-blue-200">Parcelamento de Acordo Detectado</h4>
                                        <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5 font-medium leading-relaxed">
                                            Encontramos a parcela <strong>{linkedParcela.numero_parcela}/{linkedParcela.parcelamento?.numero_parcelas || '?'}</strong> pendente para esta referência, no valor de <strong>R$ {Number(linkedParcela.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>. Os campos de descrição e acréscimo foram preenchidos automaticamente para inclusão no boleto.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {bloqueioVigor && (
                                <div className="p-4 bg-red-50 dark:bg-red-950/40 border border-red-100 dark:border-red-900/50 rounded-2xl flex gap-3.5 items-start animate-fade-in">
                                    <div className="p-2.5 bg-red-500/10 rounded-xl text-red-600 dark:text-red-400 mt-0.5 shrink-0">
                                        <AlertTriangle size={20} />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-bold text-red-900 dark:text-red-200">A Vigor não cobra esta fatura</h4>
                                        <p className="text-xs text-red-700 dark:text-red-300 mt-0.5 font-medium leading-relaxed">{bloqueioVigor}</p>
                                    </div>
                                </div>
                            )}

                            {avisoTroca && (
                                <div className="p-4 bg-amber-50 dark:bg-amber-950/40 border border-amber-100 dark:border-amber-900/50 rounded-2xl flex gap-3.5 items-start animate-fade-in">
                                    <div className="p-2.5 bg-amber-500/10 rounded-xl text-amber-600 dark:text-amber-400 mt-0.5 shrink-0">
                                        <AlertTriangle size={20} />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-bold text-amber-900 dark:text-amber-200">Troca de dono da UC</h4>
                                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5 font-medium leading-relaxed">{avisoTroca}</p>
                                    </div>
                                </div>
                            )}

                            {/* Preview não-bloqueante de quem vai ser cobrado: o boleto Vigor sai no
                                nome do assinante do termo, nunca no titular da conta de luz na
                                Equatorial — ver src/lib/pagadorBoleto.ts. */}
                            {pagadorPreview ? (
                                <div className="p-4 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-100 dark:border-emerald-900/50 rounded-2xl flex gap-3.5 items-start animate-fade-in">
                                    <div className="p-2.5 bg-emerald-500/10 rounded-xl text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0">
                                        <CheckCircle size={20} />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-bold text-emerald-900 dark:text-emerald-200">Este boleto vai ser cobrado de</h4>
                                        <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-0.5 font-medium leading-relaxed">
                                            <strong>{pagadorPreview.nome}</strong> — {pagadorPreview.documento} (fonte: {pagadorPreview.origem}). Confira se é mesmo o assinante antes de calcular.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="p-4 bg-amber-50 dark:bg-amber-950/40 border border-amber-100 dark:border-amber-900/50 rounded-2xl flex gap-3.5 items-start animate-fade-in">
                                    <div className="p-2.5 bg-amber-500/10 rounded-xl text-amber-600 dark:text-amber-400 mt-0.5 shrink-0">
                                        <AlertTriangle size={20} />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-bold text-amber-900 dark:text-amber-200">Assinante não identificado</h4>
                                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5 font-medium leading-relaxed">
                                            Esta UC não tem negociação/termo vinculado nem cadastro de cliente próprio. A emissão vai bloquear até isso ser corrigido em Unidades.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* O que o Financeiro mudou no cadastro desde o último boleto, com o motivo. */}
                            <AlteracoesCadastroUc ucId={ucId} faturaId={fatura.id} ucCodigo={fatura.uc_codigo} />

                            {/* General Info / Config Section */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <InputGroup
                                    label="Nome do Cliente"
                                    name="cliente_nome"
                                    value={formData.cliente_nome}
                                    onChange={handleInputChange}
                                    type="text"
                                    disabled={!isEditing}
                                />
                                <InputGroup
                                    label="CPF/CNPJ"
                                    name="cliente_cpf_cnpj"
                                    value={formData.cliente_cpf_cnpj}
                                    onChange={handleInputChange}
                                    type="text"
                                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                                    disabled={!isEditing}
                                />
                                <InputGroup
                                    label="Código da UC"
                                    name="uc_codigo"
                                    value={formData.uc_codigo}
                                    onChange={handleInputChange}
                                    type="text"
                                    disabled={!isEditing}
                                />
                                <CampoPercentual
                                    label="Desconto Tarifa (%)"
                                    value={rascunhoDesconto.desconto_tarifa ?? fracaoParaPercentTexto(formData.desconto_tarifa)}
                                    onChange={texto => handleDescontoChange('desconto_tarifa', texto)}
                                    onFocus={() => handleDescontoFocus('desconto_tarifa')}
                                    onBlur={() => handleDescontoBlur('desconto_tarifa')}
                                    erro={erroDesconto('desconto_tarifa')}
                                    disabled={!isEditing}
                                />
                                <CampoPercentual
                                    label="Desconto Bandeira (%)"
                                    value={rascunhoDesconto.desconto_bandeira ?? fracaoParaPercentTexto(formData.desconto_bandeira)}
                                    onChange={texto => handleDescontoChange('desconto_bandeira', texto)}
                                    onFocus={() => handleDescontoFocus('desconto_bandeira')}
                                    onBlur={() => handleDescontoBlur('desconto_bandeira')}
                                    erro={erroDesconto('desconto_bandeira')}
                                    disabled={!isEditing}
                                />
                                <div className="relative">
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Vencimento do Boleto</label>
                                    <input
                                        type="date"
                                        name="vencimento_boleto"
                                        value={formData.vencimento_boleto ? formData.vencimento_boleto.split('T')[0] : ''}
                                        onChange={handleInputChange}
                                        disabled={!isEditing}
                                        className={`w-full bg-white dark:bg-gray-800 border rounded-lg py-2 px-3 text-sm font-medium outline-none transition-all ${!isEditing ? 'border-transparent text-gray-500 cursor-not-allowed' : 'border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary'}`}
                                    />
                                    {regraVencimento && (
                                        <p data-regra-vencimento className="text-[11px] text-sky-700 dark:text-sky-300 mt-1 font-medium">
                                            Regra da UC: {regraVencimento.texto}{regraVencimento.aplicada ? ' — preenchido por ela.' : '.'}
                                        </p>
                                    )}
                                </div>
                                <InputGroup
                                    label="Descrição do Parcelamento"
                                    name="parcelamento_descricao"
                                    value={formData.parcelamento_descricao}
                                    onChange={handleInputChange}
                                    type="text"
                                    placeholder="Ex: Parcela 1/5 - Acordo"
                                    disabled={!isEditing}
                                />
                                <InputGroup
                                    label="Valor do Parcelamento"
                                    name="valor_parcelamentos_boleto"
                                    value={formData.valor_parcelamentos_boleto}
                                    onChange={handleInputChange}
                                    disabled={!isEditing}
                                />
                            </div>

                            {ucId ? (
                                <div className="space-y-3">
                                    {descontoForaDoCadastro ? (
                                        <div className="p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-100 dark:border-amber-900/50 rounded-xl flex gap-2.5 items-start">
                                            <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                                            <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
                                                O desconto desta fatura é diferente do cadastro da UC ({textoDescontos(descontoCadastro)}).{' '}
                                                {alcanceDestaFatura ? (
                                                    <>Registrado: <strong>{rotuloAlcance(alcanceDestaFatura)}</strong>{alcanceDestaFatura.alterado_por_nome ? `, por ${alcanceDestaFatura.alterado_por_nome}` : ''}.</>
                                                ) : (
                                                    <>Antes de seguir, o sistema pergunta se vale só nesta fatura ou daqui pra frente.</>
                                                )}
                                            </p>
                                        </div>
                                    ) : null}
                                    <ListaHistoricoDesconto registros={historico.registros} carregando={historico.carregando} erro={historico.erro} />
                                </div>
                            ) : null}

                            <div className="h-px bg-gray-100 dark:bg-gray-700 my-4"></div>

                            {/* Calculations Display */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <ValueDisplay label="Valor Consumo SCEE" value={calculated.valor_consumo_scee_boleto} />
                                <ValueDisplay label="Valor Adc. Bandeira" value={calculated.valor_adc_bandeira_boleto} />
                                <ValueDisplay label="Valor Parc. Injet. s/ Desc." value={calculated.valor_parc_injet_s_desc_boleto} />
                                <ValueDisplay label="Valor Restituição Imposto" value={calculated.valor_restituicao_imposto_boleto} />
                                <ValueDisplay label="Valor Energia Não Isenta" value={calculated.valor_energia_nao_isenta_boleto} />
                                <ValueDisplay label="Valor Juros" value={calculated.valor_juros_boleto} />
                                <ValueDisplay label="Multas/Outros" value={calculated.outros_multas_boleto} />
                                <ValueDisplay label="Parcelamentos" value={calculated.valor_parcelamentos_boleto} />
                            </div>

                        </div>

                        {/* Footer */}
                        <div className="p-8 bg-gray-50 dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 flex flex-col md:flex-row justify-between items-center gap-6">

                            <div className="flex flex-col items-center md:items-start">
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Confirme o valor do <strong>boleto da Vigor Energy</strong></p>
                                <div className="text-4xl font-black text-gray-900 dark:text-white tracking-tight">
                                    R$ {calculated.valor_boleto_vigor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                            </div>

                            <div className="flex items-center gap-3 w-full md:w-auto flex-wrap justify-end">
                                <button
                                    onClick={handleBack}
                                    disabled={loading || isEditing}
                                    className="text-warning border border-warning hover:bg-warning/80 hover:text-white px-4 py-3 rounded-xl font-bold hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
                                >
                                    <ArrowLeft size={16} /> Retroceder etapa
                                </button>

                                {!isEditing ? (
                                    <button
                                        onClick={() => setIsEditing(true)}
                                        className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-white px-4 py-3 rounded-xl font-bold hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
                                    >
                                        <Calculator size={16} /> Corrigir dados
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleStopEditing}
                                        className="bg-primary/10 border border-primary text-primary px-4 py-3 rounded-xl font-bold hover:bg-primary/20 transition-colors flex items-center gap-2"
                                    >
                                        <Save size={16} /> Concluir edição
                                    </button>
                                )}

                                <button
                                    onClick={() => handleConfirm()}
                                    disabled={loading || isEditing || !!bloqueioVigor}
                                    className="bg-blue-600 text-white px-5 py-3 rounded-xl font-bold shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40 hover:-translate-y-0.5 transition-all flex items-center gap-2 disabled:opacity-50"
                                >
                                    <CheckCircle size={16} /> Confirmar cálculo
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

const InputGroup = ({ label, name, value, onChange, disabled, type = 'number', placeholder }: any) => (
    <div className="relative">
        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">{label}</label>
        <input
            type={type}
            step={type === 'number' ? "0.0001" : undefined}
            name={name}
            value={value}
            onChange={onChange}
            disabled={disabled}
            placeholder={placeholder}
            className={`w-full bg-white dark:bg-gray-800 border rounded-lg py-2 px-3 text-sm font-medium outline-none transition-all ${disabled ? 'border-transparent text-gray-500 cursor-not-allowed' : 'border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary'}`}
        />
    </div>
);

// Texto (não type="number"): o campo numérico do navegador devolve "" para "28," a
// meio caminho, e o parseFloat || 0 de antes zerava o campo a cada vírgula.
const CampoPercentual = ({ label, value, onChange, onFocus, onBlur, erro, disabled }: {
    label: string;
    value: string;
    onChange: (texto: string) => void;
    onFocus: () => void;
    onBlur: () => void;
    erro: string | null;
    disabled: boolean;
}) => (
    <div className="relative">
        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">{label}</label>
        <div className="relative">
            <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={value}
                onChange={e => onChange(e.target.value)}
                onFocus={onFocus}
                onBlur={onBlur}
                disabled={disabled}
                aria-invalid={!!erro}
                className={`w-full bg-white dark:bg-gray-800 border rounded-lg py-2 pl-3 pr-8 text-sm font-medium outline-none transition-all ${disabled ? 'border-transparent text-gray-500 cursor-not-allowed' : erro ? 'border-red-400 dark:border-red-500 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-200' : 'border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary'}`}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400 pointer-events-none">%</span>
        </div>
        {erro && !disabled ? <p className="mt-1 text-[11px] font-medium text-red-600 dark:text-red-400">{erro}</p> : null}
    </div>
);

const ValueDisplay = ({ label, value }: any) => (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">{label}</label>
        <div className="text-xl font-bold text-gray-900 dark:text-white truncate">
            R$ {Number(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
    </div>
);

export default FaturaCalculationModal;
